import { TrendingUp, Building2, Layers, BarChart2 } from 'lucide-react';
import { useLingui } from '@lingui/react';
import type { PortfolioSummary, UserExchange } from '../../api/client';

interface StatCardsProps {
  summary: PortfolioSummary | null;
  exchanges: UserExchange[];
}

export function StatCards({ summary, exchanges }: StatCardsProps) {
  const { _ } = useLingui();
  const syncedCount = exchanges.filter((e) => e.syncStatus === 'ok').length;

  const pnl =
    summary && summary.totalValueInCurrency > 0
      ? summary.totalValueInCurrency - summary.totalCostInCurrency
      : null;

  const pnlPct =
    pnl !== null && summary && summary.totalCostInCurrency > 0
      ? (pnl / summary.totalCostInCurrency) * 100
      : null;

  const isPos = pnl !== null && pnl >= 0;

  return (
    <div className="overflow-x-auto rounded-2xl shadow border border-base-300">
      <div className="stats stats-vertical sm:stats-horizontal w-full bg-base-100 min-w-120">
        {/* Toplam Pozisyon */}
        <div className="stat">
          <div className="stat-figure text-primary hidden sm:flex">
            <Layers size={32} strokeWidth={2.25} aria-hidden="true" />
          </div>
          <div className="stat-title">{_({ id: 'dashboard.stats.totalPositions.title' })}</div>
          <div className="stat-value">{summary?.totalPositions ?? 0}</div>
          <div className="stat-desc">{_({ id: 'dashboard.stats.totalPositions.desc' })}</div>
        </div>

        {/* Bağlı Exchange */}
        <div className="stat">
          <div className="stat-figure text-info hidden sm:flex">
            <Building2 size={32} strokeWidth={2.25} aria-hidden="true" />
          </div>
          <div className="stat-title">{_({ id: 'dashboard.stats.exchanges.title' })}</div>
          <div className="stat-value">{exchanges.length}</div>
          <div className="stat-desc">
            {_({ id: 'dashboard.stats.exchanges.desc', values: { count: syncedCount } })}
          </div>
        </div>

        {/* Portföy Değeri */}
        <div className="stat">
          <div className="stat-figure text-accent hidden sm:flex">
            <BarChart2 size={32} strokeWidth={2.25} aria-hidden="true" />
          </div>
          <div className="stat-title">
            {_({
              id: 'dashboard.stats.portfolioValue.title',
              values: { currency: summary?.displayCurrency ? `(${summary.displayCurrency})` : '' },
            })}
          </div>
          <div className="stat-value text-2xl">
            {summary && summary.totalValueInCurrency > 0
              ? summary.totalValueInCurrency.toLocaleString('tr-TR', { maximumFractionDigits: 0 })
              : '—'}
          </div>
          <div
            className={`stat-desc ${
              pnl !== null ? (isPos ? 'text-success' : 'text-error') : 'text-base-content/40'
            }`}
          >
            {pnl !== null && pnlPct !== null
              ? `${isPos ? '+' : ''}${pnl.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} (${isPos ? '+' : ''}${pnlPct.toFixed(2)}%)`
              : _({ id: 'dashboard.stats.portfolioValue.noData' })}
          </div>
        </div>

        {/* Toplam Maliyet */}
        <div className="stat">
          <div className="stat-figure text-warning hidden sm:flex">
            <TrendingUp size={32} strokeWidth={2.25} aria-hidden="true" />
          </div>
          <div className="stat-title">
            {_({
              id: 'dashboard.stats.totalCost.title',
              values: { currency: summary?.displayCurrency ? `(${summary.displayCurrency})` : '' },
            })}
          </div>
          <div className="stat-value text-2xl">
            {summary && summary.totalCostInCurrency > 0
              ? summary.totalCostInCurrency.toLocaleString('tr-TR', { maximumFractionDigits: 0 })
              : '—'}
          </div>
          <div className="stat-desc">{_({ id: 'dashboard.stats.totalCost.desc' })}</div>
        </div>
      </div>
    </div>
  );
}
